import { ComponentProps, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { SingleSelect } from "@bonliva-crm/ui";
import { GDPROptionType } from "@bonliva-crm/shared";
import { useLocale, useUpdateEffect } from "@hooks";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { isFilterSelected } from "../utils/is-filter-selected";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { SingleValueChip } from "./_select/single-value-chip";

export type GDPRIntegrationPolicyFilterData = GDPROptionType;

type GDPRIntegrationPolicyFilterProps = FilterWithFavouriteProps;
type Option = { label?: string; value: string };

export const GDPRIntegrationPolicyFilter = (props: GDPRIntegrationPolicyFilterProps) => {
  const { locale } = useLocale();

  const options = [
    { label: locale.Yes, value: GDPROptionType.Yes },
    { label: locale.notSpecified, value: GDPROptionType.NotSpecified }
  ];

  const selectRef = useRef<HTMLInputElement>(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title =
    props.label ?? locale.GDPR + ": " + locale.gdprConsentType.IntegrationPolicy;

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current?.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const value = {
    label: options.find((option) => option.value === field?.value)?.label,
    value: field?.value
  };

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options,
    value,
    name: props.name,
    onChange: (selected) => {
      setValue(props.name, selected?.value, { shouldDirty: true });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: isFilterSelected(field?.value),
    optionType: "radio",
    isClearable: isFilterSelected(field?.value),
    onClear: () => setValue(props.name, null, { shouldDirty: true })
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <SingleSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          openMenuOnFocus
          menuPortalTarget={document.body}
          controlShouldRenderValue
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          components={{
            SingleValue: (p) => (
              <SingleValueChip {...p} onDelete={() => resetField(props.name)} />
            )
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <SingleSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
